'use client';

import {
  ArrowUpwardsIcon,
  AskAiChatHeaderBrandIcon,
  CrossIcon,
  DocsPageIcon,
  ExpandPanelIcon,
  NewChatIcon
} from '@/components/Icons/Icons';
import MaterialIcons from '@/components/MaterialIcons/materialIcons';
import { normalizeDocsHref } from '@/helpers/docsUrl';
import { cn } from '@/lib/utils';
import type { ConciergeSource } from '@contentstack/agents-sdk';
import { useCallback, useEffect, useId, type ReactNode, type RefObject } from 'react';
import { Streamdown } from 'streamdown';
import 'streamdown/styles.css';
import type { ChatMessage } from './ask-ai-types';

// Constants
const EMPTY_STATE_HINT = 'Ask a question below...';
const SCROLL_CLASSES =
  'ask-ai-scroll [&::-webkit-scrollbar]:rounded-none [&::-webkit-scrollbar-track]:rounded-none [&::-webkit-scrollbar-thumb]:rounded-none';
const CONTENTSTACK_SITE_ORIGIN = 'https://www.contentstack.com';

// Animation Components
function ThinkingDots({ className }: { className?: string }) {
  return (
    <div className={cn('thinking-dots', className)}>
      <div className="dot" />
      <div className="dot" />
      <div className="dot" />
    </div>
  );
}

// UI Components
function HeaderButton({
  children,
  label,
  onClick,
  pressed,
  className,
}: {
  children: ReactNode;
  label: string;
  onClick?: () => void;
  pressed?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={pressed}
      aria-label={label}
      className={cn(
        'focus-visible:ring-amethyst-accent flex h-6 w-6 cursor-pointer items-center justify-center rounded-lg text-white/60 focus-visible:ring-2 focus-visible:ring-offset-2',
        className
      )}
    >
      {children}
    </button>
  );
}

/** Header “new chat” glyph (Figma): speech bubble + plus; uses currentColor for toolbar token + hover. */

// Simplified utility functions
const formatSegment = (segment: string) => {
  try {
    return decodeURIComponent(segment)
      .replace(/\+/g, ' ')
      .split('-')
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  } catch {
    return segment;
  }
};

const hasHttpProtocol = (value: string) => /^https?:\/\//i.test(value);
const hasAnyProtocol = (value: string) => /^[a-z][a-z0-9+.-]*:/i.test(value);
const isDocsPathname = (pathname: string) => pathname === '/docs' || pathname.startsWith('/docs/');

const toContentstackAbsoluteHref = (path: string) =>
  `${CONTENTSTACK_SITE_ORIGIN}${path.startsWith('/') ? '' : '/'}${path}`;

const resolveSourceHref = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (hasHttpProtocol(trimmed)) return trimmed;
  if (hasAnyProtocol(trimmed) || trimmed.startsWith('#') || trimmed.startsWith('?')) {
    return null;
  }

  const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;

  if (isDocsPathname(path) || /^\/docs(?=\/|$)/i.test(path)) {
    const docsHref = normalizeDocsHref(path);
    return hasHttpProtocol(docsHref) ? docsHref : toContentstackAbsoluteHref(docsHref);
  }

  return toContentstackAbsoluteHref(path);
};

const parseHref = (href: string) => {
  try {
    const url = new URL(href, CONTENTSTACK_SITE_ORIGIN);
    const segments = url.pathname.split('/').filter(Boolean);
    const title =
      segments.length > 0
        ? formatSegment(segments[segments.length - 1]!)
        : url.hostname.replace(/^www\./, '');

    const breadcrumbs = segments.slice(0, -1).map(formatSegment).filter(Boolean).slice(0, 4);

    return { title, breadcrumbs, urlLine: url.href };
  } catch {
    return { title: href, breadcrumbs: [], urlLine: href };
  }
};

const isVideoLink = (href: string) => /youtube\.com|youtu\.be|vimeo\.com|\.mp4(\?|$)/i.test(href);

const normalizeSourceTitle = (value: string) => {
  // Remove leading "[...]-" prefixes, e.g. "[Brand Kit] - About Brand Kit".
  const cleaned = value.replace(/^\[[^\]]+\]\s*-\s*/u, '').trim();
  return cleaned.length > 0 ? cleaned : value;
};

// Simplified message grouping
function groupMessagesIntoTurns(messages: ChatMessage[]): ChatMessage[][] {
  const turns: ChatMessage[][] = [];

  for (let i = 0; i < messages.length; i++) {
    const current = messages[i]!;
    const next = messages[i + 1];

    if (current.role === 'user' && next?.role === 'assistant') {
      turns.push([current, next]);
      i++; // Skip next message since we already included it
    } else {
      turns.push([current]);
    }
  }

  return turns;
}

function SourceRow({ source, href }: { source: ConciergeSource; href?: string }) {
  const isLink = !!href;

  // For links, parse the URL for rich display
  const linkData = isLink ? parseHref(href) : null;
  const breadcrumbLine = linkData?.breadcrumbs.join(' > ') || '';

  // Determine display content
  const sourceKeyRaw = source.key?.trim();
  const sourceKey =
    sourceKeyRaw && sourceKeyRaw.toLowerCase() !== 'url' ? normalizeSourceTitle(sourceKeyRaw) : '';

  const displayTitle = isLink ? sourceKey || linkData?.title || '' : source.key;
  const displayUrl = isLink ? linkData?.urlLine : source.value;

  // Choose appropriate icon
  const icon = isLink ? (
    <DocsPageIcon className="text-amethyst-accent size-6" />
  ) : (
    <DocsPageIcon className="text-amethyst-accent size-6" />
  );

  const content = (
    <>
      <div className="flex shrink-0 items-start justify-center pt-1">{icon}</div>
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <p className="font-inter text-text-default-medium truncate text-sm leading-tight font-medium">
          {displayTitle}
        </p>

        {/* Show breadcrumbs for links */}
        {isLink && breadcrumbLine.length > 0 && (
          <p className="font-inter text-text-default-light mt-1 truncate text-xs leading-4">
            {breadcrumbLine}
          </p>
        )}

        {/* Show content type for non-links */}
        {!isLink && source.contentType && (
          <p className="font-inter text-[var(--Text-Inverse-Light)] mt-0.5 text-xs">{source.contentType}</p>
        )}

        <p className="font-inter mt-0.5 text-text-default-light truncate text-xs font-normal">
          {displayUrl}
        </p>
      </div>
    </>
  );

  // Render as link or div based on whether href is provided
  if (isLink) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="hover:bg-surface-3 focus-visible:ring-amethyst-accent flex w-full gap-2 rounded-lg px-0 py-2 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
      >
        {content}
      </a>
    );
  }

  return <div className="flex w-full gap-3 rounded-lg py-2">{content}</div>;
}

type MessageItemProps = {
  msg: ChatMessage;
  isAnimating: boolean;
  sourcesExpanded: boolean;
  onToggleSources: (messageId: string) => void;
};

function MessageItem({ msg, isAnimating, sourcesExpanded, onToggleSources }: MessageItemProps) {
  const sourcesPanelId = useId();
  const sourceCount = msg.sources?.length || 0;

  if (msg.role === 'user') {
    return (
      <div className="flex w-full justify-end">
        <div className="bg-amethyst-accent flex min-h-[36px] max-w-[85%] items-start rounded-2xl px-4 py-3">
          <p className="font-inter text-[14px] leading-[1.4] font-normal tracking-[0px] break-words text-white">
            {msg.content}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <div
        className="ask-ai-assistant-prose docs-redesign-article font-inter [&_a]:text-amethyst-accent hover:[&_a]:text-amethyst-light w-full text-sm [&_a]:underline [&_li]:!mb-0 [&_ol]:!list-decimal [&_strong]:font-semibold"
        style={
          {
            color: 'var(--color-text-default-light)',
            '--tw-prose-strong': 'var(--color-text-default-medium)',
            '--tw-prose-links': 'var(--color-text-action-medium)',
          } as React.CSSProperties
        }
      >
        <div className="streamdown-contents">
          <Streamdown isAnimating={isAnimating} linkSafety={{ enabled: false }}>
            {msg.content || '\u200b'}
          </Streamdown>
        </div>
      </div>

      {/* {sourceCount > 0 && (
        <div className="flex w-full flex-col gap-0">
          <button
            type="button"
            onClick={() => onToggleSources(msg.id)}
            className="font-inter flex h-6 w-full cursor-pointer items-center justify-between border-none bg-transparent px-0 py-0 text-xs font-medium transition-colors"
            style={
              {
                color: 'var(--color-text-default-light)',
                '--tw-hover-text': 'var(--color-text-default-medium)',
              } as React.CSSProperties
            }
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--color-text-default-medium)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-text-default-light)')}
            aria-expanded={sourcesExpanded}
            aria-controls={sourcesPanelId}
          >
            <div className="flex items-center gap-1">
              <span>
                {sourceCount} {sourceCount === 1 ? 'Source' : 'Sources'}
              </span>
              <MaterialIcons
                iconName={sourcesExpanded ? 'expand_less' : 'expand_more'}
                className="text-text-default-light -rotate-360 text-[16px]"
              />
            </div>
          </button>

          <div
            id={sourcesPanelId}
            className={cn(
              'ask-ai-scroll',
              SCROLL_CLASSES,
              'flex flex-col transition-[max-height,opacity] duration-200 ease-in-out',
              sourcesExpanded
                ? 'max-h-[280px] overflow-y-auto opacity-100'
                : 'max-h-0 overflow-hidden opacity-0'
            )}
          >
            {sourcesExpanded &&
              msg.sources!.map((source, index) => {
                const href = resolveSourceHref(source.value);
                return (
                  <SourceRow
                    key={`${source.key}:${source.value}:${index}`}
                    source={source}
                    href={href || undefined}
                  />
                );
              })}
          </div>
        </div>
      )} */}

      {/* Action buttons - matches Figma bottom tray */}
      {/* <div className="flex w-full items-center justify-end mt-4">
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="flex h-11 w-11 items-center justify-center text-icon-subtle hover:text-icon-medium hover:bg-surface-3 transition-colors rounded-lg focus-visible:ring-2 focus-visible:ring-amethyst-accent focus-visible:ring-offset-2"
            aria-label="Rate response as helpful"
          >
            <MaterialIcons iconName="thumb_up" className="text-[16px]" />
          </button>
          <button
            type="button"
            className="flex h-11 w-11 items-center justify-center text-icon-subtle hover:text-icon-medium hover:bg-surface-3 transition-colors rounded-lg focus-visible:ring-2 focus-visible:ring-amethyst-accent focus-visible:ring-offset-2"
            aria-label="Rate response as unhelpful"
          >
            <MaterialIcons iconName="thumb_down" className="text-[16px]" />
          </button>
          <button
            type="button"
            className="flex h-11 w-11 items-center justify-center text-icon-subtle hover:text-icon-medium hover:bg-surface-3 transition-colors rounded-lg focus-visible:ring-2 focus-visible:ring-amethyst-accent focus-visible:ring-offset-2"
            aria-label="Copy response to clipboard"
          >
            <MaterialIcons iconName="content_copy" className="text-[16px]" />
          </button>
        </div>
      </div> */}
    </div>
  );
}

export type AskAiChatHeaderProps = {
  onNewChat: () => void;
  onClose: () => void;
  panelExpanded?: boolean;
  onTogglePanelExpand?: () => void;
};

export function AskAiChatHeader({
  onNewChat,
  onClose,
  panelExpanded = false,
  onTogglePanelExpand,
}: AskAiChatHeaderProps) {
  return (
    <header className="bg-surface-4 flex h-14 w-full items-center justify-between px-8 py-4">
      <div className="flex items-center gap-2.5">
        <AskAiChatHeaderBrandIcon />
        <h2
          className="font-inter text-sm font-semibold tracking-[0.14px]"
          style={{ color: 'var(--color-text-default-medium)' }}
        >
          AI Assistant
        </h2>
      </div>

      <div className="flex items-center gap-2.5">
        {onTogglePanelExpand && (
          <HeaderButton
            label={panelExpanded ? 'Use narrow panel' : 'Expand panel'}
            onClick={onTogglePanelExpand}
            pressed={panelExpanded}
          >
            <ExpandPanelIcon />
          </HeaderButton>
        )}
        <HeaderButton label="New chat" onClick={onNewChat}>
          <NewChatIcon />
        </HeaderButton>
        <HeaderButton label="Close" onClick={onClose}>
          <CrossIcon />
        </HeaderButton>
      </div>
    </header>
  );
}

export type AskAiChatBodyProps = {
  messages: ChatMessage[];
  isLoading: boolean;
  showLoadingDots: boolean;
  sourcesExpandedByMessageId: Record<string, boolean>;
  onToggleSources: (messageId: string) => void;
  streamingMessageId: string | null | undefined;
  messagesEndRef: RefObject<HTMLDivElement | null>;
  chatScrollRef?: RefObject<HTMLDivElement | null>;
  autoScrollEnabledRef?: React.MutableRefObject<boolean>;
  emptyStateHint?: string;
  // Footer props
  formRef: RefObject<HTMLDivElement | null>;
  onSubmit: () => void;
  inputValue: string;
  onInputChange: (value: string) => void;
  placeholder: string;
  submitDisabled: boolean;
};

export function AskAiChatBody({
  messages,
  isLoading,
  showLoadingDots,
  sourcesExpandedByMessageId,
  onToggleSources,
  streamingMessageId,
  messagesEndRef,
  chatScrollRef,
  autoScrollEnabledRef,
  emptyStateHint = EMPTY_STATE_HINT,
  // Footer props
  formRef,
  onSubmit,
  inputValue,
  onInputChange,
  placeholder,
  submitDisabled,
}: AskAiChatBodyProps) {
  const turns = groupMessagesIntoTurns(messages);

  // Simplified scroll detection
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!autoScrollEnabledRef) return;

      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50;
      autoScrollEnabledRef.current = isNearBottom;
    },
    [autoScrollEnabledRef]
  );

  // Reset auto-scroll for new messages
  useEffect(() => {
    if (streamingMessageId && autoScrollEnabledRef) {
      autoScrollEnabledRef.current = true;
    }
  }, [streamingMessageId, autoScrollEnabledRef]);

  return (
    <div className="relative flex flex-1 flex-col overflow-hidden">
      {/* Surface Gradient Overlay - matches Figma */}
      <div className="pointer-events-none absolute z-10" />

      <div
        ref={chatScrollRef}
        className={cn('ask-ai-scroll', SCROLL_CLASSES, 'flex-1 overflow-y-auto px-8')}
        onScroll={handleScroll}
        role="log"
        aria-live="polite"
        aria-label="Chat conversation"
      >
        <div
          className={cn('flex w-full flex-col', messages.length === 0 && 'min-h-full justify-end')}
        >
          {messages.length === 0 && (
            <div className="flex w-full justify-start" role="status" aria-live="polite">
              <p
                className="font-inter text-[14px] leading-[1.5] font-normal"
                style={{ color: 'var(--color-text-default-light)' }}
              >
                {emptyStateHint}
              </p>
            </div>
          )}

          {turns.map((turn) => (
            <div key={turn[0]!.id} className="flex w-full flex-col gap-4 pt-8">
              {turn.map((msg) => (
                <MessageItem
                  key={msg.id}
                  msg={msg}
                  isAnimating={streamingMessageId === msg.id}
                  sourcesExpanded={sourcesExpandedByMessageId[msg.id] || false}
                  onToggleSources={onToggleSources}
                />
              ))}
            </div>
          ))}

          {showLoadingDots && (
            <div className="flex pt-4">
              <div className="px-4 py-3">
                <ThinkingDots />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      <AskAiChatFooter
        formRef={formRef}
        onSubmit={onSubmit}
        inputValue={inputValue}
        onInputChange={onInputChange}
        placeholder={placeholder}
        isLoading={isLoading}
        submitDisabled={submitDisabled}
      />
    </div>
  );
}

export type AskAiChatFooterProps = {
  formRef: RefObject<HTMLDivElement | null>;
  onSubmit: () => void;
  inputValue: string;
  onInputChange: (value: string) => void;
  placeholder: string;
  isLoading: boolean;
  submitDisabled: boolean;
};

export function AskAiChatFooter({
  formRef,
  onSubmit,
  inputValue,
  onInputChange,
  placeholder,
  isLoading,
  submitDisabled,
}: AskAiChatFooterProps) {
  const handleSubmit = () => {
    if (submitDisabled) return;
    onSubmit();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      handleSubmit();
    }
  };

  return (
    <div className="from-surface-1/0 to-surface-1 flex flex-col gap-2 bg-gradient-to-b to-[27.434%] px-4 pt-8 pb-4">
      <div
        ref={formRef}
        className="bg-surface-4 flex h-[40px] w-full items-center rounded-full px-5"
        data-ask-ai-component="input-container"
      >
        <input
          type="text"
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={isLoading}
          className="ask-ai-input font-inter flex-1 bg-transparent text-[14px] leading-[44px] font-normal outline-none disabled:opacity-60"
          style={
            {
              color: 'var(--color-text-default-strong)',
            } as React.CSSProperties
          }
          aria-label="Chat message"
          autoComplete="off"
          spellCheck="false"
          data-ask-ai-component="chat-input"
        />
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit();
          }}
          disabled={submitDisabled}
          className="text-icon-subtle hover:text-icon-medium hover:bg-surface-3 focus-visible:ring-amethyst-accent flex h-10 w-10 items-center justify-center rounded-full transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-40"
          aria-label="Send message"
        >
          <ArrowUpwardsIcon />
        </button>
      </div>

      <div className="ml-4 flex items-center gap-1">
        <MaterialIcons iconName="info" className="text-icon-subtle align-middle !text-[16px]" />
        <span
          className="text-[12px] leading-[1.4] font-medium"
          style={{ color: 'var(--color-text-default-subtle)' }}
        >
          AI responses may contain mistakes.
        </span>
      </div>
    </div>
  );
}
